import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Calendar, Dumbbell, Clock } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import HamburgerMenu from '../components/HamburgerMenu';

const Activity = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/workouts/history?limit=50`);
      setHistory(response.data.history || []);
    } catch (error) {
      toast.error('Failed to load workout history');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-950 via-stone-900 to-black pb-20 overflow-x-hidden">
      <HamburgerMenu />
      
      <div className="max-w-5xl mx-auto px-4 py-4 animate-fade-in">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-blood-400 tracking-wider">WORKOUT ACTIVITY</h1>
          <p className="text-stone-500 uppercase tracking-widest text-xs mt-1">Your training records</p>
        </div>

        {loading ? (
          <Card className="glass p-8 text-center border border-stone-800">
            <p className="text-blood-400 text-xl font-bold tracking-wider">LOADING WORKOUT RECORDS...</p>
          </Card>
        ) : history.length === 0 ? (
          <Card className="glass p-8 text-center border border-stone-800">
            <Dumbbell className="w-16 h-16 text-blood-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-blood-400 mb-2 tracking-wider">NO WORKOUTS YET</h2>
            <p className="text-stone-400 mb-4 uppercase tracking-wider">Your first training session awaits!</p>
            <Button
              onClick={() => navigate('/hub')}
              className="bg-gradient-to-r from-blood-700 to-blood-900 hover:from-blood-600 hover:to-blood-800 text-white font-bold tracking-wider"
            >
              Start Training
            </Button>
          </Card>
        ) : (
          <div className="space-y-4">
            <Card className="glass p-4 border border-stone-800">
              <div className="flex items-center justify-between text-blood-400 text-sm mb-2 font-bold">
                <span className="uppercase tracking-wider">Total Workouts Completed: {history.length}</span>
              </div>
            </Card>

            {history.map((workout, idx) => (
              <Card key={workout.id || idx} className="glass p-6 hover:bg-stone-800/50 transition-colors border border-stone-800">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-blood-700 to-blood-900 flex items-center justify-center">
                        <Dumbbell className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-blood-400 tracking-wider uppercase">
                          {workout.workout_type || workout.muscle_group || 'Training Session'}
                        </h3>
                        <div className="flex items-center gap-2 text-sm text-stone-400">
                          <Calendar className="w-4 h-4" />
                          <span>{formatDate(workout.date)}</span>
                        </div>
                      </div>
                    </div>

                    {workout.exercises && workout.exercises.length > 0 && (
                      <div className="ml-13 mt-3">
                        <p className="text-stone-300 text-sm mb-2 font-semibold uppercase tracking-wider">
                          {workout.exercises.length} exercise{workout.exercises.length > 1 ? 's' : ''} completed
                        </p>
                        <div className="space-y-1">
                          {workout.exercises.slice(0, 3).map((exercise, exIdx) => (
                            <div key={exIdx} className="text-stone-400 text-sm flex items-center gap-2">
                              <span className="w-1 h-1 bg-blood-500 rounded-full"></span>
                              <span>{exercise.exercise_name}</span>
                              {exercise.sets && (
                                <span className="text-blood-500">
                                  ({exercise.sets.length} set{exercise.sets.length > 1 ? 's' : ''})
                                </span>
                              )}
                            </div>
                          ))}
                          {workout.exercises.length > 3 && (
                            <p className="text-blood-500 text-xs font-bold">
                              +{workout.exercises.length - 3} more exercises
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {workout.completed && (
                    <div className="glass px-3 py-1 rounded-lg border border-emerald-700">
                      <span className="text-emerald-400 text-sm font-semibold">✓ COMPLETED</span>
                    </div>
                  )}
                </div>

                {workout.notes && (
                  <div className="mt-4 glass p-3 rounded-lg border border-stone-800">
                    <p className="text-stone-300 text-sm">{workout.notes}</p>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Activity;
